import {
  DateRange,
  FrequencyUnit,
  HabitSchedule,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  Day,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  Interval,
  isBefore,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { weekdaysNumberMap } from '../constants';
import { getScheduleFrequency } from '../get-schedule-frequency';
import { getScheduleInterval } from '../get-schedule-interval';
import { isBetween } from '../lib/date-fns';
import { RRule } from '../lib/rrule';
import { getActualScheduleStartDate } from './get-actual-schedule-start-date';
import { getScheduleEndDate } from './get-schedule-end-date';

const getFirstOccurrence = (schedule: HabitSchedule) => {
  if (schedule.frequency?.count) {
    return schedule.startDate;
  }

  const rrule = RRule.fromString(schedule.rrule.format);
  const dtstart = startOfDay(schedule.startDate);
  return new RRule({
    ...rrule.origOptions,
    dtstart,
  }).after(dtstart, true);
};

const clampToScheduleStart = (
  timeframeEnd: Timestamp,
  scheduleStartDate: Timestamp,
) =>
  new Date(
    Math.min(
      ...(isBefore(timeframeEnd, scheduleStartDate)
        ? [endOfDay(addDays(scheduleStartDate, -1)).getTime()]
        : []),
      timeframeEnd.getTime(),
    ),
  );

// the grid period holding `date`, before the schedule's own endDate clamps it
const getScheduleTimeframeGrid = (
  schedule: HabitSchedule,
  date: Timestamp,
  weekStartsOn: WeekDays,
): DateRange => {
  const frequency = getScheduleFrequency(schedule);
  const scheduleStartDate = startOfDay(schedule.startDate);
  const scheduleStart = isBefore(date, schedule.startDate)
    ? getActualScheduleStartDate(schedule)
    : scheduleStartDate;
  const interval = getScheduleInterval(schedule);
  // get the first occurrence of the schedule to have the correct calculations of the timeframe
  const firstOccurrenceAfterScheduleStart = getFirstOccurrence(schedule);
  const firstOccurrence = isBefore(date, schedule.startDate)
    ? scheduleStart
    : firstOccurrenceAfterScheduleStart ?? scheduleStart;

  if (frequency === FrequencyUnit.Year) {
    const dateStart = startOfYear(date);
    // take the interval off the first occurrence to get the start of the timeframe
    const firstTimeframeStart = addYears(
      startOfYear(firstOccurrence),
      -(interval - 1),
    );
    const diffInYears = differenceInYears(dateStart, firstTimeframeStart);
    const diffInYearsOffset = Math.floor(diffInYears / interval) * interval;
    const dateIntervalStart = startOfYear(
      addYears(firstTimeframeStart, diffInYearsOffset),
    );
    const yearStart = new Date(
      Math.max(firstTimeframeStart.getTime(), dateIntervalStart.getTime()),
    );
    const timeframeEnd = endOfYear(addYears(yearStart, interval - 1));

    return {
      startDate: yearStart,
      endDate: clampToScheduleStart(timeframeEnd, scheduleStartDate),
    };
  }

  if (frequency === FrequencyUnit.Month) {
    const dateStart = startOfMonth(date);
    // take the interval off the first occurrence to get the start of the timeframe
    const firstTimeframeStart = addMonths(
      startOfMonth(firstOccurrence),
      -(interval - 1),
    );
    const diffInMonths = differenceInMonths(dateStart, firstTimeframeStart);
    const diffInMonthsOffset = Math.floor(diffInMonths / interval) * interval;
    const dateIntervalStart = startOfMonth(
      addMonths(firstTimeframeStart, diffInMonthsOffset),
    );
    const monthStart = new Date(
      Math.max(firstTimeframeStart.getTime(), dateIntervalStart.getTime()),
    );
    const timeframeEnd = endOfMonth(addMonths(monthStart, interval - 1));

    return {
      startDate: monthStart,
      endDate: clampToScheduleStart(timeframeEnd, scheduleStartDate),
    };
  }

  const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
  const dateStart = startOfWeek(date, dateFnsOptions);
  // take the interval off the first occurrence to get the start of the timeframe
  const firstTimeframeStart = addWeeks(
    startOfWeek(firstOccurrence, dateFnsOptions),
    -(interval - 1),
  );
  // whole weeks from the grid origin to `date` — sets the interval phase
  const diffInWeeks = differenceInWeeks(dateStart, firstTimeframeStart);
  // weeks `date` sits into its interval period; `(% + ) %` keeps it >= 0 for pre-origin dates
  const weeksIntoInterval = ((diffInWeeks % interval) + interval) % interval;
  // from dateStart, not addWeeks(origin, n): the far-past hop drifts over DST and snaps to the prior week, dropping due habits
  const dateIntervalStart = addWeeks(dateStart, -weeksIntoInterval);
  const weekStart = new Date(
    Math.max(firstTimeframeStart.getTime(), dateIntervalStart.getTime()),
  );
  const timeframeEnd = endOfWeek(
    addWeeks(weekStart, interval - 1),
    dateFnsOptions,
  );

  return {
    startDate: weekStart,
    endDate: clampToScheduleStart(timeframeEnd, scheduleStartDate),
  };
};

export const getScheduleTimeframeByDate = (
  schedule: HabitSchedule,
  date: Timestamp,
  weekStartsOn: WeekDays,
): DateRange => {
  const { startDate, endDate } = getScheduleTimeframeGrid(
    schedule,
    date,
    weekStartsOn,
  );
  const scheduleEnd = getScheduleEndDate(schedule);

  return {
    startDate,
    endDate: new Date(
      Math.min(
        ...(scheduleEnd ? [scheduleEnd.getTime()] : []),
        endDate.getTime(),
      ),
    ),
  };
};

// off the grid end, not the clamped end — the latter resolves back to itself
export const getNextScheduleTimeframe = (
  schedule: HabitSchedule,
  date: Timestamp,
  weekStartsOn: WeekDays,
): DateRange =>
  getScheduleTimeframeByDate(
    schedule,
    addDays(getScheduleTimeframeGrid(schedule, date, weekStartsOn).endDate, 1),
    weekStartsOn,
  );

const frequencyEachMap: Record<
  FrequencyUnit,
  (interval: Interval, options: { weekStartsOn: Day }) => Timestamp[]
> = {
  [FrequencyUnit.Week]: (interval, options) =>
    eachWeekOfInterval(interval, options),
  [FrequencyUnit.Month]: (interval) => eachMonthOfInterval(interval),
  [FrequencyUnit.Year]: (interval) => eachYearOfInterval(interval),
};

export const getScheduleTimeframesByDateRange = (
  schedule: HabitSchedule,
  {
    startDate,
    endDate,
    weekStartsOn,
  }: { startDate: Timestamp; endDate: Timestamp; weekStartsOn: WeekDays },
): DateRange[] => {
  const frequency = getScheduleFrequency(schedule);
  const eachOf = frequencyEachMap[frequency] ?? eachWeekOfInterval;

  return eachOf(
    { start: startDate, end: endDate },
    { weekStartsOn: weekdaysNumberMap[weekStartsOn] },
  ).reduce<DateRange[]>((acc, date) => {
    const alreadyInTimeframe = acc.some(({ startDate, endDate }) =>
      isBetween(date, startDate, endDate),
    );

    if (alreadyInTimeframe) {
      return acc;
    }

    const dateRange = getScheduleTimeframeByDate(schedule, date, weekStartsOn);
    const dateRangeAlreadyInResult = acc.some(
      ({ startDate, endDate }) =>
        startDate === dateRange.startDate && endDate === dateRange.endDate,
    );

    if (dateRangeAlreadyInResult) {
      return acc;
    }

    acc.push(dateRange);
    return acc;
  }, []);
};
